import { Divider } from '@ui/themed';
import { t } from 'i18next';
import { useCallback } from 'react';
import { SettingToggleRow } from '../../../../components/setting-toggle-row';
import { SettingsSection } from '../../../../components/settings-section';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { CART_SEARCH_TYPES, DOCS_URLS } from '../../constants';
import { useStorefrontOptionsControls } from '../../context/storefront-options-context';
import type { CheckoutElement } from '../../types/spark-options';
import { CheckoutElementsEditor } from '../checkout-elements-editor';
import { OptionsCard } from '../options-card';
import { PaymentMethodsOrderEditor } from '../payment-methods-order-editor';
import { SelectField } from '../select-field';

const CARD =
  'features:storefront_options.pages.storefront_options_page.cards.checkout_settings' satisfies I18nPrefix;

export function CheckoutSettingsCard() {
  const { options, set, siteSettings } = useStorefrontOptionsControls();

  const handleElementsChange = useCallback(
    (values: CheckoutElement[]) => set('checkoutCustomElements', values),
    [set],
  );

  const cartSearchTypeOptions = CART_SEARCH_TYPES.map((value) => ({
    value,
    label: t(`${CARD}.fields.cart_search_type.options.${value}`),
  }));

  return (
    <OptionsCard title={t(`${CARD}.title`)} subtitle={t(`${CARD}.subtitle`)}>
      <SettingsSection
        title={t(`${CARD}.sections.checkout_elements.title`)}
        description={t(`${CARD}.sections.checkout_elements.description`)}
        learnMoreUrl={DOCS_URLS.checkout.checkoutElements}
      >
        <CheckoutElementsEditor
          values={options.checkoutCustomElements}
          onChange={handleElementsChange}
        />
      </SettingsSection>

      <Divider />

      <SettingsSection
        title={t(`${CARD}.sections.payment_methods_order.title`)}
        description={t(`${CARD}.sections.payment_methods_order.description`)}
        learnMoreUrl={DOCS_URLS.checkout.paymentMethodsOrder}
      >
        <PaymentMethodsOrderEditor
          values={options.paymentMethodsOrder}
          onChange={(values) => set('paymentMethodsOrder', values)}
        />
      </SettingsSection>

      <Divider />

      <SettingsSection title={t(`${CARD}.sections.cart_and_addresses.title`)}>
        <SettingToggleRow
          label={t(`${CARD}.fields.save_to_address_book.label`)}
          description={t(`${CARD}.fields.save_to_address_book.description`)}
          checked={options.saveToAddressBookDefault}
          onChange={(checked) => set('saveToAddressBookDefault', checked)}
          learnMoreUrl={DOCS_URLS.checkout.saveToAddressBook}
        />
        <SelectField
          label={t(`${CARD}.fields.cart_search_type.label`)}
          description={t(`${CARD}.fields.cart_search_type.description`)}
          value={siteSettings.values.cartSearchType}
          options={cartSearchTypeOptions}
          onChange={(value) => siteSettings.set('cartSearchType', value)}
          learnMoreUrl={DOCS_URLS.checkout.cartSearchType}
        />
        <SettingToggleRow
          label={t(`${CARD}.fields.hide_ai_cart_features.label`)}
          description={t(`${CARD}.fields.hide_ai_cart_features.description`)}
          checked={siteSettings.values.hideAICartFeatures}
          onChange={(value) => siteSettings.set('hideAICartFeatures', value)}
          learnMoreUrl={DOCS_URLS.checkout.hideAICartFeatures}
        />
      </SettingsSection>
    </OptionsCard>
  );
}
